import {
  isFailedPreconditionError,
  isUnauthenticatedError,
} from '@aboutyou/data-fetcher';
import {useTranslation} from '@aboutyou/i18n';
import {Log} from '@aboutyou/mlm';
import {useBasketRoute} from '@aboutyou/router';
import {useShowToast} from '@aboutyou/ui-components';
import {useEffect, useRef} from 'react';
import {useHistory} from 'react-router';
import {useAuthenticationPrompt} from '../../../features/Authentication/AuthenticationProvider/useAuthenticationPrompt';
import {
  DiscountNotificationType,
  useDiscountNotification,
} from '../../Campaigns/hooks/useDiscountNotification';
import {useDiscounts} from '../../Campaigns/hooks/useDiscounts';
import {CheckoutAnimation} from './CheckoutAnimation/CheckoutAnimation';

export const CheckoutErrorView = ({error}: {error?: unknown}) => {
  const {push} = useHistory();
  const basketPage = useBasketRoute();
  const showToast = useShowToast();
  const {t} = useTranslation();
  const {showNotification: showDiscountNotification} =
    useDiscountNotification();
  const {refetch: refetchDiscounts} = useDiscounts();

  const {showUserAuthenticationPrompt} = useAuthenticationPrompt();

  const errorLogged = useRef(false);

  CheckoutAnimation.End();

  // eslint-disable-next-line local-rules/too-complex-useeffects
  useEffect(() => {
    if (errorLogged.current) return;
    errorLogged.current = true;

    // the error object is empty in cases we show the fallback view in AuthenticatedView component.
    if (!error) {
      // The only error handling we need to do is redirect back to the Basket
      push(basketPage);
      // Should also show a toast explaining what went wrong
      showToast({
        type: 'ERROR',
        content: t('Sorry, we could not authenticate you'),
      });
      Log.error('@aboutyou/logic-components', 'CheckoutErrors', {
        error_message:
          'User entered the checkout page without without being authenticated',
        error_code: -1,
      });
      return;
    }

    Log.error('@aboutyou/logic-components', 'CheckoutErrors', {
      error: error as Error,
    });

    if (isFailedPreconditionError(error)) {
      showDiscountNotification({
        type: DiscountNotificationType.DiscountDeActivated,
      });

      // refetch the discounts and send the user back to the basket so that they see the updated prices before they go to checkout again.
      refetchDiscounts().then(() => {
        push(basketPage);
      });
      return;
    }

    if (isUnauthenticatedError(error)) {
      showToast({
        type: 'ERROR',
        content: t('Sorry, we could not authenticate you'),
      });
      showUserAuthenticationPrompt({
        context: 'checkout-page-logged-in-user-unauthenticated',
        needsCheckoutSession: true,
        forceSessionRefresh: true, // the user is already logged-in, but to fix this CO Auth error we need the user to re-login
        onCancel: () => {
          // eslint-disable-next-line local-rules/set-state-in-use-effect -- the cancel of the auth modal does a history back thus cancelling the basket page push
          // the setTimeout is needed to ensure the the user is pushed to the basket page after the auth modal is closed
          setTimeout(() => {
            push(basketPage);
          }, 100);
        },
      });
      return;
    }

    // Unhandled case where the error code doesn't translate to a meaningful error case. Like error code 2
    showToast({
      type: 'ERROR',
      content: t('Oops! Something went wrong!'),
    });
    push(basketPage);
  }, []);

  return null;
};
